import { createAsyncThunk } from '@reduxjs/toolkit';
import { tenantService, TenantCreateRequest, TenantUpdateRequest, TenantAssignUserRequest } from '../../services/api/tenantService';

export const fetchTenants = createAsyncThunk(
  'tenant/fetchTenants',
  async (includeUserCount: boolean = false, { rejectWithValue }) => {
    try {
      return await tenantService.getTenants(includeUserCount);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch tenants');
    }
  }
);

/** Fetch tenants scoped to a specific user's assignments, including forceWorkspaceHome flag. */
export const fetchUserTenants = createAsyncThunk(
  'tenant/fetchUserTenants',
  async (userId: number, { rejectWithValue }) => {
    try {
      return await tenantService.getTenantsWithConfig(userId);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch user tenants');
    }
  }
);

export const fetchTenant = createAsyncThunk(
  'tenant/fetchTenant',
  async (id: number, { rejectWithValue }) => {
    try {
      return await tenantService.getTenant(id);
    } catch (error: any) {
      return rejectWithValue(error.message ?? 'Failed to fetch tenant');
    }
  }
);

export const createTenant = createAsyncThunk(
  'tenant/createTenant',
  async (request: TenantCreateRequest, { rejectWithValue }) => {
    try {
      return await tenantService.createTenant(request);
    } catch (error: any) {
      return rejectWithValue(error.message ?? 'Failed to create tenant');
    }
  }
);

export const updateTenant = createAsyncThunk(
  'tenant/updateTenant',
  async ({ id, request }: { id: number; request: TenantUpdateRequest }, { rejectWithValue }) => {
    try {
      await tenantService.updateTenant(id, request);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message ?? 'Failed to update tenant');
    }
  }
);

export const deleteTenant = createAsyncThunk(
  'tenant/deleteTenant',
  async (id: number, { rejectWithValue }) => {
    try {
      await tenantService.deleteTenant(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message ?? 'Failed to delete tenant');
    }
  }
);

export const assignUserToTenant = createAsyncThunk(
  'tenant/assignUser',
  async ({ tenantId, request }: { tenantId: number; request: TenantAssignUserRequest }, { rejectWithValue }) => {
    try {
      await tenantService.assignUser(tenantId, request);
      return tenantId;
    } catch (error: any) {
      return rejectWithValue(error.message ?? 'Failed to assign user');
    }
  }
);

export const removeUserFromTenant = createAsyncThunk(
  'tenant/removeUser',
  async ({ tenantId, userId }: { tenantId: number; userId: number }, { rejectWithValue }) => {
    try {
      await tenantService.removeUser(tenantId, userId);
      return { tenantId, userId };
    } catch (error: any) {
      return rejectWithValue(error.message ?? 'Failed to remove user');
    }
  }
);
